import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { apiFetcher } from '@/data/fetcher'
import { ParameterFormModal } from '@/forms/ParameterForm/ParameterFormModal'
import { Parameter } from '@/interfaces/catalog/Parameter'
import { ProductParameterInput } from '@/lib/catalog/productStructure'

interface ParameterChecklistProps {
  value: ProductParameterInput[]
  onChange: (parameters: ProductParameterInput[]) => void
}

export const ParameterChecklist = ({ value, onChange }: ParameterChecklistProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'catalog',
  })
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  const { data: response, mutate } = useSWR<{ data?: Parameter[]; total?: number }>(
    '/api/v2/parameters',
    apiFetcher,
  )
  const parameters = response?.data || []

  const toggle = (parameterId: string, checked: boolean) =>
    onChange(
      checked
        ? [...value, { parameterId, isRequired: false, parameterValueId: null, rawValue: null }]
        : value.filter((item) => item.parameterId !== parameterId),
    )

  const update = (parameterId: string, change: Partial<ProductParameterInput>) =>
    onChange(
      value.map((item) => (item.parameterId === parameterId ? { ...item, ...change } : item)),
    )

  return (
    <div className="space-y-4">
      {parameters.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t('products.detail.parameters.assign.empty')}
        </p>
      ) : (
        parameters.map((parameter) => {
          const assignment = value.find((item) => item.parameterId === parameter.id)
          const hasValues = (parameter.values?.length ?? 0) > 0
          return (
            <div key={parameter.id} className="space-y-2">
              <div className="flex items-center gap-3">
                <Checkbox
                  id={`parameter-${parameter.id}`}
                  checked={assignment !== undefined}
                  onCheckedChange={(checked) => toggle(parameter.id, checked === true)}
                />
                <Label htmlFor={`parameter-${parameter.id}`} className="font-normal">
                  {parameter.name}
                  <span className="text-muted-foreground ml-2">({parameter.code})</span>
                </Label>
              </div>
              {assignment && (
                <div className="ml-7 space-y-2">
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`parameter-required-${parameter.id}`}
                      checked={assignment.isRequired}
                      onCheckedChange={(checked) => update(parameter.id, { isRequired: checked })}
                    />
                    <Label htmlFor={`parameter-required-${parameter.id}`} className="font-normal">
                      {t('products.detail.parameters.assign.required')}
                    </Label>
                  </div>
                  {parameter.isDescriptive && hasValues && (
                    <Select
                      value={assignment.parameterValueId ?? ''}
                      onValueChange={(parameterValueId) => update(parameter.id, { parameterValueId })}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue
                          placeholder={t('products.detail.parameters.assign.valuePlaceholder')}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {parameter.values.map((option) => (
                          <SelectItem key={option.id ?? option.code} value={option.id ?? option.code}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {parameter.isDescriptive && !hasValues && parameter.valueType === 'boolean' && (
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`parameter-value-${parameter.id}`}
                        checked={assignment.rawValue === '1'}
                        onCheckedChange={(checked) =>
                          update(parameter.id, { rawValue: checked ? '1' : '0' })
                        }
                      />
                      <Label htmlFor={`parameter-value-${parameter.id}`} className="font-normal">
                        {t('products.detail.parameters.assign.value')}
                      </Label>
                    </div>
                  )}
                  {parameter.isDescriptive && !hasValues && parameter.valueType !== 'boolean' && parameter.valueType !== 'select' && (
                    <Input
                      type={parameter.valueType === 'color' ? 'color' : parameter.valueType === 'number' ? 'number' : 'text'}
                      value={assignment.rawValue ?? ''}
                      placeholder={t('products.detail.parameters.assign.value')}
                      onChange={(event) => update(parameter.id, { rawValue: event.target.value || null })}
                    />
                  )}
                </div>
              )}
            </div>
          )
        })
      )}

      <Button type="button" variant="ghost" size="sm" onClick={() => setIsCreateOpen(true)}>
        <Plus className="size-4" />
        {t('parameters.actions.create')}
      </Button>

      {/* React bubbles submit through the portal, so without this the modal would also submit an outer product form */}
      <div onSubmit={(event) => event.stopPropagation()}>
        <ParameterFormModal
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          onSuccess={() => mutate()}
        />
      </div>
    </div>
  )
}
